import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Calendar,
  Clock,
  Scissors,
  MapPin,
  User,
  Phone,
  MessageSquare,
  Edit2,
  X,
  DollarSign,
  AlertTriangle,
  CheckCircle,
  Download,
  Upload,
  Camera,
  Image,
  FileImage,
  CreditCard,
  Receipt
} from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL;

const AppointmentDetailModal = ({ 
  appointment, 
  isOpen, 
  onClose, 
  onUpdate
}) => {
  const navigate = useNavigate();
  const [showCancelDialog, setShowCancelDialog] = useState(false);
  const [loading, setLoading] = useState(false);
  const [stylistInfo, setStylistInfo] = useState(null);
  const [message, setMessage] = useState("");
  const [showMessageForm, setShowMessageForm] = useState(false);
  const [receiptPreview, setReceiptPreview] = useState(null);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    if (appointment && isOpen) {
      fetchStylistInfo();
      setReceiptPreview(null);
    }
  }, [appointment, isOpen]);

  const fetchStylistInfo = async () => {
    if (!appointment?.stylist_id) return;
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(
        `${API}/api/stylists/${appointment.stylist_id}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setStylistInfo(response.data);
    } catch (error) {
      console.error("Failed to fetch stylist info:", error);
    }
  };

  const handleEditAppointment = () => {
    // Navigate to booking page with appointment data for rescheduling
    navigate('/book', { 
      state: { 
        reschedule: true,
        appointmentId: appointment.appointment_id,
        existingData: {
          stylist_id: appointment.stylist_id,
          service_id: appointment.service_id,
          date: appointment.date,
          time: appointment.time,
          stylist_name: appointment.stylist?.name || stylistInfo?.user?.name,
          service_name: appointment.service_name || appointment.service?.name,
          // Carry over payment info
          payment_method: appointment.payment_method,
          payment_status: appointment.payment_status,
          receipt_status: appointment.receipt_status,
          receipt_image: appointment.receipt_image,
          total_amount: appointment.total_amount || appointment.price
        }
      }
    });
    onClose();
  };

  const handleCancelAppointment = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("token");
      await axios.put(
        `${API}/api/appointments/${appointment.appointment_id}/cancel`,
        { reason: "Client requested cancellation" },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success("Appointment cancelled. Refund will be processed if applicable.");
      setShowCancelDialog(false);
      onClose();
      onUpdate?.();
    } catch (error) {
      console.error("Cancel error:", error);
      toast.error(error.response?.data?.detail || "Failed to cancel appointment");
    } finally {
      setLoading(false);
    }
  };

  const handleSendMessage = async () => {
    if (!message.trim()) return;
    
    setLoading(true);
    try {
      const token = localStorage.getItem("token");
      await axios.post(
        `${API}/api/mailbox/send`,
        {
          recipient_id: appointment.stylist_id,
          appointment_id: appointment.appointment_id,
          message: message.trim()
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success("Message sent to your stylist!");
      setMessage("");
      setShowMessageForm(false);
    } catch (error) {
      console.error("Message error:", error);
      toast.error("Failed to send message");
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadCalendar = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await fetch(
        `${API}/api/appointments/${appointment.appointment_id}/calendar`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (!response.ok) throw new Error("Failed to download");
      
      const text = await response.text();
      const blob = new Blob([text], { type: 'text/calendar;charset=utf-8' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `appointment_${appointment.appointment_id}.ics`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      toast.success("Calendar event downloaded! Open it to add to your calendar.");
    } catch (error) {
      console.error("Calendar download error:", error);
      toast.error("Failed to download calendar event");
    }
  };

  // Handle file selection for receipt upload
  const handleFileSelect = (e, fromCamera = false) => {
    const file = e.target.files?.[0];
    if (file) {
      if (file.size > 10 * 1024 * 1024) {
        toast.error("File too large. Maximum size is 10MB.");
        return;
      }
      
      const reader = new FileReader();
      reader.onloadend = () => {
        setReceiptPreview({
          file,
          dataUrl: reader.result
        });
      };
      reader.readAsDataURL(file);
    }
  };

  // Upload receipt
  const handleUploadReceipt = async () => {
    if (!receiptPreview?.file) {
      toast.error("Please select a receipt image");
      return;
    }

    setUploading(true);
    try {
      const token = localStorage.getItem("token");
      const formData = new FormData();
      formData.append("appointment_id", appointment.appointment_id);
      formData.append("payment_method", appointment.payment_method);
      formData.append("amount", (appointment.total_amount || appointment.price || 0).toString());
      formData.append("receipt_image", receiptPreview.file);

      const response = await axios.post(
        `${API}/api/payments/receipt/upload`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "multipart/form-data"
          }
        }
      );

      // Check if AI verification passed
      const aiResult = response.data.ai_verification;
      if (aiResult?.is_valid_receipt && aiResult?.confidence === 'high') {
        toast.success("Receipt verified by AI! Payment confirmed.");
      } else if (aiResult?.is_valid_receipt) {
        toast.success("Receipt uploaded! Awaiting stylist verification.");
      } else {
        toast.warning(aiResult?.rejection_reason || "Receipt uploaded but may need review.");
      }
      
      setReceiptPreview(null);
      onUpdate?.();
    } catch (error) {
      console.error("Upload error:", error);
      toast.error(error.response?.data?.detail || "Failed to upload receipt");
    } finally {
      setUploading(false);
    }
  };

  // Mark appointment as prepaid
  const handleMarkAsPrepaid = async () => {
    if (!appointment.receipt_uploaded && !appointment.receipt_image) {
      toast.error("Please upload a receipt first before marking as prepaid");
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem("token");
      await axios.post(
        `${API}/api/payments/mark-prepaid/${appointment.appointment_id}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success("Marked as prepaid! Awaiting stylist verification.");
      onUpdate?.();
    } catch (error) {
      console.error("Mark prepaid error:", error);
      toast.error(error.response?.data?.detail || "Failed to mark as prepaid");
    } finally {
      setLoading(false);
    }
  };

  // Request manual review from stylist when AI rejects
  const handleRequestManualReview = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("token");
      
      // Send message to stylist requesting manual review
      await axios.post(
        `${API}/api/mailbox/send`,
        {
          recipient_id: appointment.stylist_id,
          appointment_id: appointment.appointment_id,
          message: `Hi! The AI receipt verification flagged my payment screenshot, but I believe this is a valid receipt. Could you please manually review and verify my payment? Thank you!`,
          subject: "Payment Receipt - Manual Review Requested"
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      // Update appointment to mark review requested
      await axios.put(
        `${API}/api/appointments/${appointment.appointment_id}/reschedule`,
        { notes: (appointment.notes || '') + '\n[CLIENT REQUESTED MANUAL RECEIPT REVIEW]' },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success("Request sent! Your stylist will manually review your receipt.");
      onUpdate?.();
    } catch (error) {
      console.error("Request review error:", error);
      toast.error("Failed to send review request");
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      confirmed: "bg-emerald-100 text-emerald-800",
      pending: "bg-amber-100 text-amber-800",
      completed: "bg-blue-100 text-blue-800",
      cancelled: "bg-red-100 text-red-800",
      no_show: "bg-gray-100 text-gray-800"
    };
    return colors[status] || "bg-gray-100 text-gray-800";
  };

  const getPaymentStatusBadge = () => {
    if (appointment.payment_method === 'stripe' && appointment.payment_status === 'paid') {
      return <Badge className="bg-emerald-100 text-emerald-800">💳 Paid with Card</Badge>;
    }
    if (appointment.payment_method === 'demo') {
      return <Badge className="bg-purple-100 text-purple-800">🎮 Demo Payment</Badge>;
    }
    if (appointment.receipt_status === 'ai_verified') {
      return <Badge className="bg-emerald-100 text-emerald-800">✓ AI Verified</Badge>;
    }
    if (appointment.receipt_status === 'verified') {
      return <Badge className="bg-emerald-100 text-emerald-800">✓ Payment Verified</Badge>;
    }
    if (appointment.receipt_status === 'ai_rejected') {
      return <Badge className="bg-orange-100 text-orange-800">⚠️ AI Flagged</Badge>;
    }
    if (appointment.receipt_status === 'pending_verification') {
      return <Badge className="bg-amber-100 text-amber-800">⏳ Awaiting Verification</Badge>;
    }
    if (appointment.receipt_status === 'rejected') {
      return <Badge className="bg-red-100 text-red-800">✗ Receipt Rejected</Badge>;
    }
    return <Badge className="bg-stone-100 text-stone-800">⏳ Payment Pending</Badge>;
  };

  const needsReceipt = () => {
    // Show receipt section for all manual payment methods (not just when missing)
    const manualMethods = ['zelle', 'cashapp', 'paypal', 'cash'];
    return manualMethods.includes(appointment.payment_method);
  };

  const canUploadReceipt = () => {
    // Can upload if receipt not yet verified
    return appointment.receipt_status !== 'verified';
  };

  const canEdit = appointment?.status === "confirmed" || appointment?.status === "pending";
  const canCancel = appointment?.status === "confirmed" || appointment?.status === "pending";

  if (!appointment) return null;

  return (
    <>
      <Dialog open={isOpen} onOpenChange={onClose}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center justify-between">
              <span className="flex items-center gap-2">
                <Scissors className="w-5 h-5 text-emerald-600" />
                Appointment Details
              </span>
              <Badge className={getStatusColor(appointment.status)}>
                {appointment.status}
              </Badge>
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            {/* Service Info */}
            <div className="bg-gradient-to-r from-emerald-50 to-teal-50 rounded-xl p-4 border border-emerald-200">
              <h3 className="font-semibold text-emerald-900 text-lg mb-1">
                {appointment.service_name || appointment.service?.name}
              </h3>
              <div className="flex items-center gap-4 text-sm text-emerald-700">
                <span className="flex items-center gap-1">
                  <Clock className="w-4 h-4" />
                  {appointment.duration_minutes || 60} min
                </span>
                <span className="flex items-center gap-1">
                  <DollarSign className="w-4 h-4" />
                  ${(appointment.total_amount || appointment.price || 0).toFixed(2)}
                </span>
              </div>
            </div>

            {/* Date & Time */}
            <div className="bg-amber-50 rounded-xl p-4 border border-amber-200">
              <h4 className="font-medium text-amber-900 mb-2 flex items-center gap-2">
                <Calendar className="w-4 h-4" />
                Date & Time
              </h4>
              <p className="text-lg font-semibold text-amber-900">
                {appointment.date} at {appointment.time}
              </p>
            </div>

            {/* Stylist/Barber Info */}
            <div className="bg-stone-50 rounded-xl p-4 border border-stone-200">
              <h4 className="font-medium text-stone-900 mb-3 flex items-center gap-2">
                <User className="w-4 h-4" />
                Your Stylist/Barber
              </h4>
              <div className="flex items-start gap-3">
                <div className="w-12 h-12 bg-emerald-100 rounded-full flex items-center justify-center flex-shrink-0">
                  <User className="w-6 h-6 text-emerald-700" />
                </div>
                <div className="flex-1">
                  <p className="font-semibold text-stone-900">
                    {appointment.stylist?.name || stylistInfo?.user?.name || "Your Stylist"}
                  </p>
                  {(stylistInfo?.business_address?.formatted || stylistInfo?.location) && (
                    <p className="text-sm text-stone-600 flex items-start gap-1 mt-1">
                      <MapPin className="w-4 h-4 flex-shrink-0 mt-0.5" />
                      <span>{stylistInfo.business_address?.formatted || stylistInfo.location}</span>
                    </p>
                  )}
                  {stylistInfo?.phone && (
                    <p className="text-sm text-stone-600 flex items-center gap-1 mt-1">
                      <Phone className="w-4 h-4" />
                      {stylistInfo.phone}
                    </p>
                  )}
                </div>
              </div>
            </div>

            {/* Payment Status Section */}
            {needsReceipt() && (
              <div className="bg-stone-50 rounded-xl p-4 border border-stone-200">
                <h4 className="font-medium text-stone-900 mb-3 flex items-center gap-2">
                  <CreditCard className="w-4 h-4" />
                  Payment Status
                </h4>
                
                <div className="space-y-3">
                  <div className="flex items-center justify-between flex-wrap gap-2">
                    <span className="text-sm text-stone-600">
                      Method: <span className="font-medium capitalize">{appointment.payment_method || 'Not selected'}</span>
                    </span>
                    {getPaymentStatusBadge()}
                  </div>

                  {/* Show existing receipt if uploaded */}
                  {appointment.receipt_image && (
                    <div className="mt-3 p-3 bg-stone-100 rounded-lg">
                      <p className="text-sm text-stone-600 mb-2 flex items-center gap-1">
                        <Receipt className="w-4 h-4" />
                        {appointment.receipt_status === 'verified' ? 'Verified Receipt:' : 'Uploaded Receipt:'}
                      </p>
                      <img 
                        src={appointment.receipt_image} 
                        alt="Payment receipt" 
                        className="w-full max-h-40 object-contain rounded-lg border border-stone-200 bg-white"
                      />
                    </div>
                  )}

                  {/* Receipt Upload Section - Show for manual payments that are not yet verified */}
                  {canUploadReceipt() && (
                    <div className={`mt-3 p-3 rounded-lg border ${
                      appointment.receipt_status === 'rejected' 
                        ? 'bg-red-50 border-red-200' 
                        : appointment.receipt_image 
                          ? 'bg-blue-50 border-blue-200'
                          : 'bg-amber-50 border-amber-200'
                    }`}>
                      <p className={`text-sm mb-3 flex items-center gap-1 ${
                        appointment.receipt_status === 'rejected'
                          ? 'text-red-800'
                          : appointment.receipt_image
                            ? 'text-blue-800'
                            : 'text-amber-800'
                      }`}>
                        {appointment.receipt_status === 'rejected' ? (
                          <>
                            <AlertTriangle className="w-4 h-4" />
                            Receipt rejected - please upload a clearer image
                          </>
                        ) : appointment.receipt_image ? (
                          <>
                            <Upload className="w-4 h-4" />
                            Upload a different receipt if needed
                          </>
                        ) : (
                          <>
                            <AlertTriangle className="w-4 h-4" />
                            Upload your payment screenshot
                          </>
                        )}
                      </p>
                      
                      {receiptPreview ? (
                        <div className="space-y-2">
                          <p className="text-xs text-stone-500 mb-1">New receipt preview:</p>
                          <img 
                            src={receiptPreview.dataUrl} 
                            alt="Receipt preview" 
                            className="w-full max-h-32 object-contain rounded-lg border border-stone-200 bg-white"
                          />
                          <div className="flex gap-2">
                            <Button
                              size="sm"
                              onClick={handleUploadReceipt}
                              disabled={uploading}
                              className="flex-1 bg-emerald-600 hover:bg-emerald-700"
                            >
                              {uploading ? "Uploading..." : appointment.receipt_image ? "Replace Receipt" : "Submit Receipt"}
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => setReceiptPreview(null)}
                            >
                              <X className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                      ) : (
                        <div className="flex gap-2">
                          {/* Take Photo Button */}
                          <label className="flex-1">
                            <div className={`flex items-center justify-center gap-2 px-3 py-2.5 rounded-lg cursor-pointer transition-colors text-sm font-medium ${
                              appointment.receipt_status === 'rejected'
                                ? 'bg-red-500 hover:bg-red-600 text-white'
                                : 'bg-amber-500 hover:bg-amber-600 text-white'
                            }`}>
                              <Camera className="w-4 h-4" />
                              Take Photo
                            </div>
                            <input
                              type="file"
                              accept="image/*"
                              capture="environment"
                              onChange={(e) => handleFileSelect(e, true)}
                              className="hidden"
                            />
                          </label>
                          
                          {/* Choose from Gallery Button */}
                          <label className="flex-1">
                            <div className="flex items-center justify-center gap-2 px-3 py-2.5 bg-purple-500 hover:bg-purple-600 text-white rounded-lg cursor-pointer transition-colors text-sm font-medium">
                              <Image className="w-4 h-4" />
                              Gallery
                            </div>
                            <input
                              type="file"
                              accept="image/*"
                              onChange={(e) => handleFileSelect(e, false)}
                              className="hidden"
                            />
                          </label>
                        </div>
                      )}
                    </div>
                  )}

                  {/* AI Rejection Notice - Show when AI flagged the receipt */}
                  {appointment.receipt_status === 'ai_rejected' && (
                    <div className="mt-3 p-3 bg-orange-50 rounded-lg border border-orange-200">
                      <div className="flex items-start gap-2 mb-3">
                        <AlertTriangle className="w-5 h-5 text-orange-600 flex-shrink-0 mt-0.5" />
                        <div>
                          <p className="text-sm font-medium text-orange-800">
                            AI Could Not Verify This Receipt
                          </p>
                          <p className="text-xs text-orange-700 mt-1">
                            Our AI system flagged this image. This can happen if the screenshot is unclear, 
                            cropped, or in an unfamiliar format. <strong>AI can make mistakes!</strong>
                          </p>
                        </div>
                      </div>
                      <div className="space-y-2">
                        <p className="text-xs text-orange-700">
                          If you believe this is a valid receipt, you can:
                        </p>
                        <div className="flex flex-col gap-2">
                          <Button
                            size="sm"
                            onClick={handleRequestManualReview}
                            disabled={loading}
                            className="w-full bg-orange-500 hover:bg-orange-600 text-white"
                          >
                            <MessageSquare className="w-4 h-4 mr-2" />
                            {loading ? "Sending..." : "Request Stylist to Review"}
                          </Button>
                          <p className="text-xs text-center text-orange-600">
                            Your stylist can manually verify and override the AI decision
                          </p>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Mark as Prepaid button - show when receipt uploaded but not yet confirmed */}
                  {(appointment.receipt_uploaded || appointment.receipt_image) && 
                   !appointment.marked_as_prepaid && 
                   appointment.receipt_status !== 'verified' &&
                   appointment.receipt_status !== 'ai_rejected' && (
                    <Button
                      onClick={handleMarkAsPrepaid}
                      disabled={loading}
                      className="w-full mt-3 bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-600 hover:to-teal-600 text-white"
                    >
                      <CheckCircle className="w-4 h-4 mr-2" />
                      {loading ? "Processing..." : "Confirm I've Sent Payment"}
                    </Button>
                  )}

                  {/* Show marked as prepaid status */}
                  {appointment.marked_as_prepaid && appointment.receipt_status !== 'verified' && (
                    <div className="mt-2 p-2 bg-blue-50 rounded-lg border border-blue-200 flex items-center gap-2 text-blue-800">
                      <CheckCircle className="w-4 h-4" />
                      <span className="text-sm font-medium">Marked as prepaid - awaiting stylist confirmation</span>
                    </div>
                  )}

                  {/* Show AI verified badge */}
                  {appointment.receipt_status === 'ai_verified' && (
                    <div className="mt-2 p-2 bg-emerald-50 rounded-lg border border-emerald-200 flex items-center gap-2 text-emerald-800">
                      <CheckCircle className="w-4 h-4" />
                      <span className="text-sm font-medium">✓ Payment verified by AI</span>
                    </div>
                  )}

                  {/* Show verified badge */}
                  {appointment.receipt_status === 'verified' && (
                    <div className="mt-2 p-2 bg-emerald-50 rounded-lg border border-emerald-200 flex items-center gap-2 text-emerald-800">
                      <CheckCircle className="w-4 h-4" />
                      <span className="text-sm font-medium">Payment verified by stylist</span>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Simple payment status for card/demo payments */}
            {!needsReceipt() && appointment.payment_method && (
              <div className="bg-stone-50 rounded-xl p-4 border border-stone-200">
                <h4 className="font-medium text-stone-900 mb-3 flex items-center gap-2">
                  <CreditCard className="w-4 h-4" />
                  Payment
                </h4>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-stone-600">
                    Method: <span className="font-medium capitalize">{appointment.payment_method}</span>
                  </span>
                  {getPaymentStatusBadge()}
                </div>
              </div>
            )}

            {/* Message Stylist */}
            {showMessageForm ? (
              <div className="bg-blue-50 rounded-xl p-4 border border-blue-200">
                <h4 className="font-medium text-blue-900 mb-2 flex items-center gap-2">
                  <MessageSquare className="w-4 h-4" />
                  Message Your Stylist
                </h4>
                <Textarea
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  placeholder="Type your message..."
                  rows={3}
                  className="mb-2"
                />
                <div className="flex gap-2">
                  <Button
                    size="sm"
                    onClick={handleSendMessage}
                    disabled={loading || !message.trim()}
                    className="bg-blue-600 hover:bg-blue-700"
                  >
                    Send Message
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => setShowMessageForm(false)}
                  >
                    Cancel
                  </Button>
                </div>
              </div>
            ) : null}

            {/* Action Buttons */}
            <div className="flex flex-wrap gap-2">
              {canEdit && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleEditAppointment}
                  className="flex-1 border-emerald-300 text-emerald-700 hover:bg-emerald-50"
                >
                  <Edit2 className="w-4 h-4 mr-1" />
                  Reschedule
                </Button>
              )}
              
              {!showMessageForm && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setShowMessageForm(true)}
                  className="flex-1"
                >
                  <MessageSquare className="w-4 h-4 mr-1" />
                  Message
                </Button>
              )}
              
              <Button
                variant="outline"
                size="sm"
                onClick={handleDownloadCalendar}
                className="flex-1"
              >
                <Download className="w-4 h-4 mr-1" />
                Calendar
              </Button>
              
              {canCancel && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setShowCancelDialog(true)}
                  className="flex-1 text-red-600 border-red-200 hover:bg-red-50"
                >
                  <X className="w-4 h-4 mr-1" />
                  Cancel
                </Button>
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Cancel Confirmation Dialog */}
      <AlertDialog open={showCancelDialog} onOpenChange={setShowCancelDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2 text-red-600">
              <AlertTriangle className="w-5 h-5" />
              Cancel Appointment?
            </AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to cancel this appointment? 
              {appointment.payment_method === "stripe" && (
                <span className="block mt-2 text-emerald-600">
                  <CheckCircle className="w-4 h-4 inline mr-1" />
                  If you paid with card, a refund will be processed.
                </span>
              )}
              {['zelle', 'cashapp', 'paypal'].includes(appointment.payment_method) && appointment.receipt_status === 'verified' && (
                <span className="block mt-2 text-amber-600">
                  <AlertTriangle className="w-4 h-4 inline mr-1" />
                  You paid via {appointment.payment_method}. Contact your stylist for refund.
                </span>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Keep Appointment</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleCancelAppointment}
              className="bg-red-600 hover:bg-red-700"
              disabled={loading}
            >
              {loading ? "Cancelling..." : "Yes, Cancel"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};

export default AppointmentDetailModal;
